import React from 'react'
import { Box, Button, Paper, Typography, useTheme } from '@mui/material'
import type { ClassicLeague } from 'types/fpl/leagues'
import type { TeamData, TeamSummary } from 'types/fpl/teams'
import type { PlayerSummary } from 'types/fpl/elements'
import LeagueSelect from 'components/structure/LeagueSelect'
import GWSelect from 'components/structure/GWSelect'
import ExcludePlayersAutocomplete from './ExcludePlayersAutocomplete'
import ExcludeTeamsAutocomplete from './ExcludeTeamsAutocomplete'
import { colors } from 'constants/colors'

interface SidebarPanelProps {
  userTeamData: TeamData | null
  leagues: ClassicLeague[]
  gws: number[]
  selectedLeague: ClassicLeague | null
  setSelectedLeague: (league: ClassicLeague | null) => void
  selectedGW: string
  setSelectedGW: (gw: string) => void
  excludedPlayers: PlayerSummary[]
  setExcludedPlayers: (players: PlayerSummary[]) => void
  excludedTeams: TeamSummary[]
  setExcludedTeams: (teams: TeamSummary[]) => void
  onCalculate: () => void
  isMobile: boolean
  loading?: boolean
  lastUpdated: number | null
  fromCache: boolean
}

const iso2ToFlag = (iso2?: string): string => {
  if (!iso2) return ''
  const code: string = iso2.toUpperCase().replace(/[^A-Z]/g, '')
  if (code.length !== 2) return ''
  return String.fromCodePoint(...code.split('').map(c => 127397 + c.charCodeAt(0)))
}

const formatNumber = (n: number): string => new Intl.NumberFormat().format(n)

const SidebarPanel: React.FC<SidebarPanelProps> = ({
  userTeamData,
  leagues,
  gws,
  selectedLeague,
  setSelectedLeague,
  selectedGW,
  setSelectedGW,
  excludedPlayers,
  setExcludedPlayers,
  excludedTeams,
  setExcludedTeams,
  onCalculate,
  isMobile,
  loading = false,
  lastUpdated,
  fromCache,
}) => {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const teamInfoContent = userTeamData ? (
    <>
      <Typography
        variant='h4'
        fontWeight='bold'
        fontFamily='PremierLeagueHeavy'
        sx={{ fontSize: { xs: '0.875rem', sm: '1.25rem', md: '1.5rem' } }}
      >
        {userTeamData.name} {iso2ToFlag(userTeamData.player_region_iso_code_short)}
      </Typography>
      <Typography
        variant='h6'
        fontFamily='PremierLeagueBold'
        sx={{ alignItems: 'middle', fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' }, mt: 0.5 }}
      >
        {userTeamData.player_first_name} {userTeamData.player_last_name}{' '}
        <span style={{ opacity: 0.65 }}>| {userTeamData.years_active}y pro</span>
      </Typography>
      <Typography
        variant='body1'
        fontFamily='PremierLeagueRegular'
        sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.9rem' }, mt: 0.75 }}
      >
        <b>Season Rank: </b> {formatNumber(userTeamData.summary_overall_rank)}
      </Typography>
      <Typography
        variant='body1'
        fontFamily='PremierLeagueRegular'
        sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.9rem' } }}
      >
        <b>Season Points:</b> {formatNumber(userTeamData.summary_overall_points)}
      </Typography>
    </>
  ) : (
    <Box sx={{ textAlign: 'center', py: 1 }}>
      <Typography
        variant='h6'
        fontFamily='PremierLeagueBold'
        sx={{ mb: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}
      >
        No Team Loaded
      </Typography>
      <Typography
        variant='body1'
        fontFamily='PremierLeagueRegular'
        sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
      >
        Return to the sign-in page to enter your FPL Team ID.
      </Typography>
      <Typography
        variant='body1'
        fontFamily='PremierLeagueRegular'
        sx={{ mt: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
      >
        (or click the website logo)
      </Typography>
    </Box>
  )

  const lastUpdatedText = fromCache && lastUpdated !== null ? (
    <Typography
      variant='caption'
      fontFamily='PremierLeagueRegular'
      color={isDark ? colors.WHITE : colors.ALMOST_BLACK}
      sx={{ display: 'block', textAlign: 'center', mt: 0.75, fontSize: { xs: '0.65rem', sm: '0.7rem' } }}
    >
      Last updated: {new Date(lastUpdated).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} at {new Date(lastUpdated).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
    </Typography>
  ) : null

  const calculateButton = (
    <Box>
      <Button
        variant='contained'
        onClick={onCalculate}
        disabled={loading}
        fullWidth
        sx={{
          fontFamily: 'PremierLeagueBold',
          bgcolor: '#2e7d32',
          color: '#fff',
          '&:hover': { bgcolor: '#1b5e20' },
          fontSize: { xs: '0.875rem', sm: '1rem' },
          py: { xs: 1, sm: 1.5 },
        }}
      >
        {loading ? 'Loading…' : 'Get Insights'}
      </Button>
      {lastUpdatedText}
    </Box>
  )

  if (isMobile) {
    return (
      <>
        {/* 10-col grid, 2 equal-height rows */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(10, 1fr)',
            gridTemplateRows: '1fr 1fr',
            height: 200,
            gap: 1,
            mb: 1,
            '& > *': { mb: '0 !important', minHeight: 0 },
            '& .MuiPaper-root': { height: '100%', boxSizing: 'border-box' },
          }}
        >
          {/* Row 1: 50% | 50% */}
          <Box sx={{ gridColumn: 'span 5' }}>
            <Paper sx={{ p: 1.5, height: '80%', boxSizing: 'border-box', overflow: 'hidden' }}>
              {teamInfoContent}
            </Paper>
          </Box>
          <Box sx={{ gridColumn: 'span 5' }}>
            <LeagueSelect
              leagues={leagues}
              selectedLeague={selectedLeague}
              setSelectedLeague={setSelectedLeague}
              isMobile={isMobile}
            />
          </Box>

          {/* Row 2: GW | Exclude Teams */}
          <Box sx={{ gridColumn: 'span 3' }}>
            <GWSelect
              gws={gws}
              selectedGW={selectedGW}
              setSelectedGW={setSelectedGW}
              isMobile={isMobile}
            />
          </Box>
          <Box sx={{ gridColumn: 'span 7' }}>
            <ExcludeTeamsAutocomplete
              excludedTeams={excludedTeams}
              setExcludedTeams={setExcludedTeams}
              isMobile={isMobile}
            />
          </Box>
        </Box>

        <ExcludePlayersAutocomplete
          excludedPlayers={excludedPlayers}
          setExcludedPlayers={setExcludedPlayers}
          isMobile={isMobile}
        />

        {calculateButton}
      </>
    )
  }

  return (
    <>
      <Paper sx={{ p: 2, mb: 2 }}>{teamInfoContent}</Paper>
      <LeagueSelect
        leagues={leagues}
        selectedLeague={selectedLeague}
        setSelectedLeague={setSelectedLeague}
        isMobile={isMobile}
      />
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'stretch', mb: 2 }}>
        <Box sx={{ flexShrink: 0, '& > .MuiPaper-root': { height: '100%', mb: 0, boxSizing: 'border-box' } }}>
          <GWSelect
            gws={gws}
            selectedGW={selectedGW}
            setSelectedGW={setSelectedGW}
            isMobile={isMobile}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, '& > .MuiPaper-root': { height: '100%', mb: 0, boxSizing: 'border-box' } }}>
          <ExcludeTeamsAutocomplete
            excludedTeams={excludedTeams}
            setExcludedTeams={setExcludedTeams}
            isMobile={isMobile}
          />
        </Box>
      </Box>
      <ExcludePlayersAutocomplete
        excludedPlayers={excludedPlayers}
        setExcludedPlayers={setExcludedPlayers}
        isMobile={isMobile}
      />
      <Box sx={{ mt: 1 }}>{calculateButton}</Box>
    </>
  )
}

export default SidebarPanel
